import { FC, useState } from 'react';
import { capitalize } from 'lodash';

import DefaultTimeView from 'pages/OfficeSchedulePage/DayTimeSlot/DefaultTimeView/DefaultTimeView';
import PracticeScheduleTooltip from './PracticeScheduleTooltip/PracticeScheduleTooltip';
import EmptySchedule from 'components/EmptySchedule/EmptySchedule';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import axiosInstance from 'apis/axiosInstance';

import OverrideModal from '../OverrideModal/OverrideModal';

import { PractitionerException } from 'interfaces/practitionerException';
import { IPractitioner } from 'interfaces/practitioners';
import { WeeklyTimeslot } from 'pages/OfficeSchedulePage/WrapperSchedulePage';

import { DAY_OFF, DEFAULT_WORKING_HOUR } from 'utils/constants';
import getChangedTimeSlots from 'utils/getChangedTimeSlots';
import { Switch } from '@headlessui/react';
import {
  PractitionerFormValue,
  PractitionerWeeklyTimeSlot,
} from '../PractitionerSchedule/PractitionerSchedule';
import PractitionerOperatorySection from '../PractitionerOperatorySection/PractitionerOperatorySection';
import { useFormContext } from 'react-hook-form';
import LunchBreakSection from './LunchBreakSection/LunchBreakSection';
import checkLunchBreakAndWorkingHour from '../../utils/checkLunchBreakAndWorkingHour';

interface DayTimeSlotProps {
  clinicTimeSlots: WeeklyTimeslot[];
  cachedTimeSlots: PractitionerWeeklyTimeSlot[];
  practitionerScheduleId: string | null;
  selectedPractitioner: IPractitioner;
  version: number;
  onCheckOutsidePractitionerHourAppts: () => Promise<void>;
}

const DayTimeSlot: FC<DayTimeSlotProps> = ({
  clinicTimeSlots,
  cachedTimeSlots,
  practitionerScheduleId,
  selectedPractitioner,
  version,
  onCheckOutsidePractitionerHourAppts,
}) => {
  const { watch, setValue } = useFormContext<PractitionerFormValue>();
  const {
    isAdvancedSchedule,
    advancedWeeklyTimeSlot,
    weeklyTimeSlot,
    advancedWeeklyTimeSlotLunchBreak,
    weeklyTimeSlotLunchBreak,
  } = watch();

  const timeSlots = isAdvancedSchedule
    ? advancedWeeklyTimeSlot
    : weeklyTimeSlot;

  const lunchBreaks = isAdvancedSchedule
    ? advancedWeeklyTimeSlotLunchBreak
    : weeklyTimeSlotLunchBreak;

  const setTimeSlots = (value: PractitionerWeeklyTimeSlot[]) => {
    setValue(
      isAdvancedSchedule ? 'advancedWeeklyTimeSlot' : 'weeklyTimeSlot',
      value,
      { shouldDirty: true }
    );
  };

  const toggleIsAdvancedSchedule = () => {
    setValue('isAdvancedSchedule', !isAdvancedSchedule, {
      shouldDirty: true,
    });
  };

  const [isSubmitting] = useState(false);

  const [exceptionDates, setExceptionDates] = useState<
    PractitionerException[] | null
  >(null);

  const changedTimeSlots = getChangedTimeSlots(timeSlots, cachedTimeSlots);

  const handleUpdatedTimeSlot = (day: string, value: string, type: string) => {
    const updatedTimeSlots = [...timeSlots];
    const timeSlot = updatedTimeSlots.find((slot) => slot.day === day)!;
    timeSlot[type as keyof WeeklyTimeslot] = value;
    const currentDayLunchBreaks = lunchBreaks.find(
      (lunchBreak) => lunchBreak.day === day
    )!;
    const result = checkLunchBreakAndWorkingHour({
      lunchBreaks: currentDayLunchBreaks,
      type: type as 'start' | 'end',
      value,
    });

    const field = isAdvancedSchedule
      ? 'advancedWeeklyTimeSlotLunchBreak'
      : 'weeklyTimeSlotLunchBreak';

    if (result.isStartLaterThanLunchBreakStart) {
      setValue(
        field,
        lunchBreaks.map((lunchBreak) => {
          if (lunchBreak.day !== day) return lunchBreak;

          return {
            ...lunchBreak,
            startFirst: value,
          };
        }),
        { shouldDirty: true }
      );
    }

    if (result.isEndEarlierThanLunchBreakEnd) {
      setValue(
        field,
        lunchBreaks.map((lunchBreak) => {
          if (lunchBreak.day !== day) return lunchBreak;

          return {
            ...lunchBreak,
            endFirst: value,
          };
        }),
        { shouldDirty: true }
      );
    }

    setTimeSlots(updatedTimeSlots);
  };

  const handleCopyTimeSlot = (timeSlots: WeeklyTimeslot[]) => {
    setTimeSlots(timeSlots);
  };

  const handleUpdateSchedule = async () => {
    const body: { [key: string]: [{ start: string; end: string }] | string } = {
      id: practitionerScheduleId!,
    };

    changedTimeSlots.forEach(({ day, end, start }) => {
      body[day as keyof typeof body] = [
        {
          start: start!,
          end: end!,
        },
      ];
    });

    await axiosInstance.patch(
      `/practitioners/${selectedPractitioner.id}/schedules`,
      body,
      {
        headers: { version },
      }
    );
  };

  return (
    <>
      {isSubmitting && <LoadingScreen />}
      {exceptionDates && (
        <OverrideModal
          exceptionDates={exceptionDates as any}
          onClose={() => setExceptionDates(null)}
          selectedPractitioner={selectedPractitioner}
          changedTimeSlots={changedTimeSlots}
          onUpdateSchedule={handleUpdateSchedule}
          version={version}
          onCheckOutsidePractitionerHourAppts={
            onCheckOutsidePractitionerHourAppts
          }
          operatories={[]}
          lunchBreaks={null}
        />
      )}
      <div className="w-full border border-light-grey border-y-0">
        <div className="px-3 h-4.4 flex items-center justify-between border-b border-light-grey">
          <div className="font-bold">Advanced</div>
          <div className="flex items-center justify-between">
            <Switch
              checked={isAdvancedSchedule}
              onChange={toggleIsAdvancedSchedule}
              className={`${
                isAdvancedSchedule ? 'bg-magenta' : 'bg-[#78788029]'
              } relative inline-flex w-4 h-2.4 items-center rounded-full`}
            >
              <span
                className={`${
                  isAdvancedSchedule
                    ? 'translate-x-[1.8rem]'
                    : 'translate-x-[2px]'
                } inline-block h-2 w-2 transform rounded-full transition bg-white`}
              />
            </Switch>
          </div>
        </div>
        {!practitionerScheduleId ? (
          <EmptySchedule />
        ) : (
          timeSlots.map(({ day, start, end }, index) => {
            const isChecked = Boolean(start && end);

            return (
              <div
                key={day}
                className="flex flex-col gap-y-1.2 border-b min-h-[6.2rem] py-1 border-light-grey "
              >
                <div className="flex items-center gap-x-3.3 flex-1">
                  <div className="flex gap-x-1.1 pl-2.1 items-center">
                    <input
                      id={`day-${day}`}
                      type="checkbox"
                      className="w-2.2 h-2.2"
                      checked={isChecked}
                      onChange={(event) => {
                        const updatedTimeSlots = [...timeSlots];
                        const timeSlot = updatedTimeSlots.find(
                          (slot) => slot.day === day
                        )!;

                        const start = cachedTimeSlots[index].start
                          ? cachedTimeSlots[index].start
                          : DEFAULT_WORKING_HOUR.START;

                        const end = cachedTimeSlots[index].end
                          ? cachedTimeSlots[index].end
                          : DEFAULT_WORKING_HOUR.END;

                        timeSlot.start = event.target.checked ? start : null;
                        timeSlot.end = event.target.checked ? end : null;
                        if (isAdvancedSchedule) {
                          timeSlot.operatories = event.target.checked
                            ? []
                            : null;
                        }
                        setTimeSlots(updatedTimeSlots);
                      }}
                    />
                    <label
                      htmlFor={`day-${day}`}
                      className="font-bold w-[3.1rem]"
                    >
                      {capitalize(day)}
                    </label>
                  </div>
                  <div className="flex items-center gap-x-0.4">
                    <PracticeScheduleTooltip
                      timeSlot={timeSlots[index]}
                      clinicTimeSlot={clinicTimeSlots[index]}
                    />
                    {!isChecked ? (
                      <span className="text-11 leading-[1.8rem]">
                        {DAY_OFF}
                      </span>
                    ) : (
                      <DefaultTimeView
                        day={day}
                        start={start!}
                        end={end!}
                        timeSlots={timeSlots}
                        onChange={handleUpdatedTimeSlot}
                        onCopy={handleCopyTimeSlot}
                      />
                    )}
                  </div>
                </div>
                {isAdvancedSchedule && isChecked && (
                  <PractitionerOperatorySection day={day} />
                )}
                {isChecked && <LunchBreakSection day={day} />}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default DayTimeSlot;
